// Integration point for search quality assessment.
//
// Builds a JSON "Search Quality Report" comparing the displayed Algolia results
// against the local engines for the current query, and emits it to the CDP
// analytics platform (plus the dev console). Algolia is treated as the gold
// standard for the content indices.
//
// Content search is scored twice per query: once for the baseline MiniSearch
// profile and once for the tuned one. Both shadows run over the same indices at
// the same moment, so the two scores form a paired comparison rather than a
// week-over-week one — which matters in a product whose traffic follows a
// school calendar.
//
// Non-content registries (navigation / external-help / quick-actions) have no
// gold standard, so they carry no score — only what they returned.

import { getDocumentType } from '../../utils/search/getDocumentType'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { SEARCH_PROFILES, TUNED_PROFILE_VERSION } from './local/buildMiniSearchIndices'
import { localProviderId } from './presentationEngine'
import { searchDebugLog } from './searchDebugLog'
import analytics from '../../utils/cdpAnalytics'

const CONTENT_INDEXES = ['repos', 'folders', 'sets_questions']
const NON_CONTENT_SOURCES = ['navigation', 'external-help', 'quick-actions']

// Round to 4 decimals; pass through non-finite/non-number values unchanged.
function round(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return n
  return Number(n.toFixed(4))
}

// Pearson correlation of two equal-length numeric arrays. Returns null when
// undefined (fewer than 2 points, or zero variance on either side).
function pearson(xs, ys) {
  const n = xs.length
  if (n < 2) return null
  const mean = (arr) => arr.reduce((acc, v) => acc + v, 0) / arr.length
  const mx = mean(xs)
  const my = mean(ys)
  let num = 0
  let dx = 0
  let dy = 0
  for (let i = 0; i < n; i += 1) {
    const a = xs[i] - mx
    const b = ys[i] - my
    num += a * b
    dx += a * a
    dy += b * b
  }
  if (dx === 0 || dy === 0) return null
  return num / Math.sqrt(dx * dy)
}

// Spearman rank correlation over the objectIDs shared by two ordered id lists,
// approximated as the Pearson correlation of their positions. Returns null when
// fewer than 2 ids are shared.
function rankCorrelation(orderA, orderB) {
  const idxB = new Map(orderB.map((id, i) => [id, i]))
  const shared = orderA.filter((id) => idxB.has(id))
  if (shared.length < 2) return null
  const idxA = new Map(orderA.map((id, i) => [id, i]))
  const xs = shared.map((id) => idxA.get(id))
  const ys = shared.map((id) => idxB.get(id))
  return pearson(xs, ys)
}

// Identify a hit for the excess/missing lists.
function describeHit(hit) {
  const documentType = getDocumentType(hit)
  const name = documentType === 'question' ? getQuestionBodyLabel(hit) : hit.name
  return { objectID: hit.objectID, name: name || '', documentType }
}

// A hit Algolia returned and the local engine did not, annotated with whether
// the document was in the local index at all:
//
//   true  — indexed but not returned: a matching or ranking failure, and the
//           only kind of miss that tuning can fix
//   false — never indexed: a loading failure, which no amount of tuning reaches
//   null  — the record shape was not recognised, so membership is unknown. This
//           must never be read as `false`
function describeMissingHit(hit, indexName, hasDocument) {
  const described = describeHit(hit)
  const inLocalIndex = typeof hasDocument === 'function' ?
    hasDocument(indexName, described.documentType, described.objectID) :
    null
  return { ...described, inLocalIndex }
}

function objectIds(hits) {
  return (Array.isArray(hits) ? hits : []).map((h) => h.objectID)
}

function getBlock(blocks, providerId) {
  if (!Array.isArray(blocks)) return null
  return blocks.find((r) => r.providerId === providerId) || null
}

function blockIndexHits(block, indexName) {
  const ir = block && block.indexResults
  const slot = ir && ir[indexName]
  return (slot && Array.isArray(slot.hits)) ? slot.hits : []
}

function getFlatHits(additiveResults, providerId) {
  const block = getBlock(additiveResults, providerId)
  return (block && Array.isArray(block.hits)) ? block.hits : []
}

// Compare one engine's hits for an index against the Algolia gold standard.
//
// Algolia is treated as the "correct" result set (the gold standard). A local
// engine (or one MiniSearch profile) is scored on how closely its results match
// Algolia's, both in membership (which objectIDs are returned) and in ordering.
// Let `overlap` = number of objectIDs the engine returned that Algolia also
// returned.
//
//   precision = overlap / (engine result count)
//       Of everything the engine returned, what fraction did Algolia also
//       return? Low precision ⇒ the engine surfaces extra results Algolia did
//       not (potential false positives / noise).
//
//   recall = overlap / (algolia result count)
//       Of everything Algolia returned, what fraction did the engine also
//       return? Low recall ⇒ the engine misses results Algolia found
//       (potential false negatives / gaps).
//
//   qualityScore = F1 = 2 * precision * recall / (precision + recall)
//       The harmonic mean of precision and recall — a single 0..1 membership
//       score that is only high when BOTH precision and recall are high. 1.0
//       means the engine returned exactly Algolia's set; 0 means no overlap.
//
//   rankCorrelation = Spearman rank correlation over the shared objectIDs
//       (approximated as the Pearson correlation of their positions in each
//       ordered list). Measures ORDERING agreement, independent of membership:
//       +1 ⇒ the engine ranks the shared results in the same order as Algolia,
//        0 ⇒ no ordering relationship, −1 ⇒ reversed order. null when fewer
//       than two results are shared (ordering is undefined).
//
// precision/recall/qualityScore answer "did we return the right items?";
// rankCorrelation answers "did we put them in the right order?".
function engineIndexMetric(algoliaHits, engineHits, indexName, hasDocument) {
  const algoliaIds = objectIds(algoliaHits)
  const engineIds = objectIds(engineHits)
  const algoliaSet = new Set(algoliaIds)
  const engineSet = new Set(engineIds)

  const excessHits = engineHits.filter((h) => !algoliaSet.has(h.objectID))
  const missingHits = algoliaHits
    .filter((h) => !engineSet.has(h.objectID))
    .map((h) => describeMissingHit(h, indexName, hasDocument))
  const overlap = engineIds.length - excessHits.length

  const precision = engineIds.length ? overlap / engineIds.length : 0
  const recall = algoliaIds.length ? overlap / algoliaIds.length : 0
  const f1 = (precision + recall) ? (2 * precision * recall) / (precision + recall) : 0
  const rho = rankCorrelation(algoliaIds, engineIds)

  return {
    excess: excessHits.length,
    missing: missingHits.length,
    // Scalar split of `missing` by cause, so attribution can be aggregated in
    // Redshift without walking the missingHits array. The three add up to
    // `missing`; the unknown remainder is deliberately not folded into either.
    missingIndexed: missingHits.filter((h) => h.inLocalIndex === true).length,
    missingNotIndexed: missingHits.filter((h) => h.inLocalIndex === false).length,
    missingUnknown: missingHits.filter((h) => h.inLocalIndex === null).length,
    excessHits: excessHits.map(describeHit),
    missingHits,
    precision: round(precision),
    recall: round(recall),
    rankCorrelation: rho == null ? null : round(rho),
    qualityScore: round(f1),
  }
}

function buildIndexMetric(algoliaHits, miniHits, tunedHits, indexName, hasDocument) {
  const algoliaSet = new Set(objectIds(algoliaHits))
  const miniSet = new Set(objectIds(miniHits))
  const countShared = (set, hits) => hits.filter((h) => set.has(h.objectID)).length
  return {
    hits: {
      algolia: algoliaHits.length,
      minisearch: miniHits.length,
      minisearchTuned: tunedHits.length,
    },
    matching: {
      algoliaMinisearch: countShared(algoliaSet, miniHits),
      algoliaMinisearchTuned: countShared(algoliaSet, tunedHits),
      // How far the tuned profile moved from the baseline, independent of
      // whether either matched Algolia. A value equal to both hit counts means
      // the tuning changed nothing on this query.
      minisearchProfiles: countShared(miniSet, tunedHits),
    },
    // Both profiles run over the same indices, so the same membership check
    // answers for both.
    minisearch: engineIndexMetric(algoliaHits, miniHits, indexName, hasDocument),
    minisearchTuned: engineIndexMetric(algoliaHits, tunedHits, indexName, hasDocument),
  }
}

// Algolia-hit-count-weighted average of an engine's per-index scores.
function weightedOverall(indices, engine) {
  const keys = Object.keys(indices)
  const totalWeight = keys.reduce((acc, k) => acc + indices[k].hits.algolia, 0)
  const divisor = totalWeight || 1
  let precision = 0
  let recall = 0
  let qualityScore = 0
  let rho = 0
  let rhoWeight = 0
  keys.forEach((k) => {
    const metric = indices[k][engine]
    const weight = indices[k].hits.algolia
    precision += metric.precision * weight
    recall += metric.recall * weight
    qualityScore += metric.qualityScore * weight
    if (metric.rankCorrelation != null) {
      rho += metric.rankCorrelation * weight
      rhoWeight += weight
    }
  })
  return {
    precision: round(precision / divisor),
    recall: round(recall / divisor),
    qualityScore: round(qualityScore / divisor),
    rankCorrelation: rhoWeight ? round(rho / rhoWeight) : null,
  }
}

function buildContentReport(resultBlocks) {
  // Algolia is located by provider id rather than by role, so it stays the gold
  // standard whether it is the display engine or a shadow. After the cutoff the
  // comparison runs in reverse and the numbers still mean the same thing.
  const algoliaBlock = getBlock(resultBlocks, 'algolia')
  const miniBlock = getBlock(resultBlocks, localProviderId('content', 'minisearch'))
  const tunedBlock = getBlock(resultBlocks, localProviderId('content', 'minisearchTuned'))

  // Supplied by the MiniSearch provider, which owns the memoised indices.
  const hasDocument = miniBlock && miniBlock.hasDocument

  const indices = {}
  CONTENT_INDEXES.forEach((indexName) => {
    const algoliaHits = blockIndexHits(algoliaBlock, indexName)
    const miniHits = blockIndexHits(miniBlock, indexName)
    const tunedHits = blockIndexHits(tunedBlock, indexName)
    indices[indexName] = buildIndexMetric(algoliaHits, miniHits, tunedHits, indexName, hasDocument)
  })

  const keys = Object.keys(indices)
  const sum = (selector) => keys.reduce((acc, k) => acc + selector(indices[k]), 0)
  const overall = {
    hits: {
      algolia: sum((m) => m.hits.algolia),
      minisearch: sum((m) => m.hits.minisearch),
      minisearchTuned: sum((m) => m.hits.minisearchTuned),
    },
    matching: {
      algoliaMinisearch: sum((m) => m.matching.algoliaMinisearch),
      algoliaMinisearchTuned: sum((m) => m.matching.algoliaMinisearchTuned),
      minisearchProfiles: sum((m) => m.matching.minisearchProfiles),
    },
    missing: {
      algoliaMinisearch: sum((m) => m.minisearch.missing),
      algoliaMinisearchTuned: sum((m) => m.minisearchTuned.missing),
    },
    // Why the baseline missed what it missed. `notIndexed` is a coverage
    // problem and `indexed` is a tuning problem, and the split decides which
    // backlog a query belongs in. See Q24 in SEARCH_QUALITY_ANALYTICS.md.
    missingAttribution: {
      indexed: sum((m) => m.minisearch.missingIndexed),
      notIndexed: sum((m) => m.minisearch.missingNotIndexed),
      unknown: sum((m) => m.minisearch.missingUnknown),
    },
    excess: {
      algoliaMinisearch: sum((m) => m.minisearch.excess),
      algoliaMinisearchTuned: sum((m) => m.minisearchTuned.excess),
    },
    minisearch: weightedOverall(indices, 'minisearch'),
    minisearchTuned: weightedOverall(indices, 'minisearchTuned'),
  }

  return { indices, overall }
}

// Non-content registries are served from MiniSearch alone. There is no remote
// equivalent to score them against, so this records what each registry returned
// rather than a quality score — enough to spot a registry that has silently
// stopped matching anything.
function buildNonContentReport(additiveResults) {
  const out = {}
  NON_CONTENT_SOURCES.forEach((base) => {
    const miniHits = getFlatHits(additiveResults, localProviderId(base, 'minisearch'))
    out[base] = {
      hits: { minisearch: miniHits.length },
      results: miniHits.map((h) => ({ objectID: h.objectID, label: h.label })),
    }
  })
  return out
}

// Per-index document counts, reported by the MiniSearch content provider.
// Both local engines only index what Redux has loaded, so without this a
// missing hit cannot be told apart from a document that was never available
// locally. Consumed by Q18 in SEARCH_QUALITY_ANALYTICS.md.
function getIndexSizes(resultBlocks) {
  const block = getBlock(resultBlocks, localProviderId('content', 'minisearch'))
  return (block && block.indexSizes) || null
}

// ─── Coverage ──────────────────────────────────────────────────────────────
//
// Three numbers per index: what the API says the account holds, what MiniSearch
// actually indexed, and what Algolia is searching. Index sizes alone cannot
// distinguish a small library from a half-loaded one, and that difference is
// the whole reason a missing hit is ambiguous.
//
// local vs expected  → did this browser finish loading the library
// expected vs algolia → do the two backends hold the same data at all
//
// Which API collections feed each search index. `sets_questions` is one index
// built from two independently loaded collections, so it is only complete when
// both of them are.
const INDEX_COLLECTIONS = {
  repos: ['repos'],
  folders: ['folders'],
  // eslint-disable-next-line camelcase
  sets_questions: ['sets', 'questions'],
}

function sumExpected(loadStatus, collections) {
  const known = collections
    .map((name) => (loadStatus[name] ? loadStatus[name].expected : null))
    .filter((expected) => typeof expected === 'number')
  if (known.length !== collections.length) return null
  return known.reduce((total, expected) => total + expected, 0)
}

// `>=` rather than `===` because browsing a repo loads content beyond the
// initial library fetch, so only indexed < expected is evidence of a problem.
//
// Returns null whenever the question cannot be answered yet — a collection that
// has not reported is not a collection that failed, and conflating the two
// would make every early-session query look like a coverage problem.
function indexComplete(indexed, expected, loadsOk) {
  if (loadsOk === false) return false
  if (loadsOk === null || indexed === null || expected === null) return null
  return indexed >= expected
}

// Strips the per-request detail the reducer keeps for its own bookkeeping.
function reportableLoads(loadStatus) {
  return Object.keys(loadStatus).reduce((acc, name) => {
    const {
      ok, failed, expected, at,
    } = loadStatus[name]
    return {
      ...acc,
      [name]: {
        ok, failed, expected, at,
      },
    }
  }, {})
}

function buildCoverage(resultBlocks, indexSizes, loadStatus) {
  const loads = loadStatus || {}
  const algoliaBlock = getBlock(resultBlocks, 'algolia')
  const corpus = (algoliaBlock && algoliaBlock.corpus) || null
  const corpusCounts = (corpus && corpus.counts) || {}

  const local = {}
  const algolia = {
    measured: corpus ? corpus.measured === true : false,
    exhaustive: corpus ? corpus.exhaustive : null,
  }
  CONTENT_INDEXES.forEach((indexName) => {
    const collections = INDEX_COLLECTIONS[indexName]
    const indexed = (indexSizes && typeof indexSizes[indexName] === 'number') ?
      indexSizes[indexName] :
      null
    const expected = sumExpected(loads, collections)
    // null when a contributing collection has not reported yet, which is not
    // the same as it having failed
    const reported = collections.filter((name) => loads[name] && loads[name].ok !== null)
    let loadsOk = null
    if (reported.length === collections.length) {
      loadsOk = collections.every((name) => loads[name].ok === true)
    }
    local[indexName] = { indexed, expected, complete: indexComplete(indexed, expected, loadsOk) }
    const count = corpusCounts[indexName]
    algolia[indexName] = typeof count === 'number' ? count : null
  })

  const perIndexComplete = CONTENT_INDEXES.map((indexName) => local[indexName].complete)
  let complete = null
  if (perIndexComplete.some((value) => value === false)) complete = false
  else if (perIndexComplete.every((value) => value === true)) complete = true

  return {
    complete, local, algolia, loads: reportableLoads(loads),
  }
}

/**
 * Build the full JSON search-quality report for the current query.
 *
 * @param {Object} input
 * @param {string} input.query
 * @param {string} input.displayedProvider    Which engine the user actually saw
 * @param {Array}  input.resultBlocks         Every content provider's block, keyed by provider id
 * @param {Array}  input.additiveResults      Additive provider results (non-content registries)
 * @param {boolean} input.algoliaSettled      Did the Algolia results belong to THIS query?
 * @param {string} input.reportReason         'settled' | 'timeout'
 * @param {string} input.surface              'topNav' | 'searchResults' | 'sectionHome'
 * @param {Object} input.limits               Requested hitsPerPage and per-engine cap
 * @param {Object} input.loadStatus           state.libraryLoadStatus
 * @returns {Object}
 */
export function buildSearchQualityReport({
  query,
  displayedProvider,
  resultBlocks,
  additiveResults,
  algoliaSettled,
  reportReason,
  surface,
  limits,
  loadStatus,
}) {
  const content = buildContentReport(resultBlocks)
  const indexSizes = getIndexSizes(resultBlocks)
  const coverage = buildCoverage(resultBlocks, indexSizes, loadStatus)
  const overallScores = [
    content.overall.minisearch.qualityScore,
    content.overall.minisearchTuned.qualityScore,
  ].filter((v) => typeof v === 'number')
  const score = {
    minisearch: content.overall.minisearch,
    minisearchTuned: content.overall.minisearchTuned,
    // Mean across the reported profiles. A health signal only — it cannot rank
    // profiles against each other, which is what score.minisearch* are for.
    overall: overallScores.length ?
      round(overallScores.reduce((acc, v) => acc + v, 0) / overallScores.length) :
      null,
  }
  return {
    query,
    displayedProvider: displayedProvider || 'algolia',
    // Measurement context. `algoliaSettled: false` means Algolia never caught up
    // with this query, so every membership number below is computed against
    // another query's results — count these rows, do not read them. Every
    // decision query filters on `algoliaSettled = true`.
    algoliaSettled: algoliaSettled !== false,
    reportReason: reportReason || 'settled',
    surface: surface || null,
    limits: limits || null,
    indexSizes,
    // Whether the local library was fully loaded when this query ran. Left
    // beside indexSizes rather than replacing it, so Q18 keeps working.
    coverage,
    // Which profile produced each set of scores below. Versioned so a later
    // tuning pass does not pool its rows with this one's.
    profiles: {
      minisearch: SEARCH_PROFILES.BASELINE,
      minisearchTuned: TUNED_PROFILE_VERSION,
    },
    content,
    score,
    nonContent: buildNonContentReport(additiveResults),
  }
}

/**
 * Build the search-quality report and ship it to the CDP analytics platform.
 * Also logs the JSON to the console in development for live inspection.
 *
 * @param {Object} report
 * @param {string} report.query
 * @param {string} report.displayedProvider
 * @param {Array}  report.resultBlocks
 * @param {Array}  report.additiveResults
 * @param {boolean} report.algoliaSettled
 * @param {string} report.reportReason
 * @param {string} report.surface
 * @param {Object} report.limits
 * @param {Object} report.loadStatus
 */
export function reportLocalVsAlgolia({
  query,
  displayedProvider,
  resultBlocks,
  additiveResults,
  algoliaSettled,
  reportReason,
  surface,
  limits,
  loadStatus,
}) {
  if (!query) return

  const report = buildSearchQualityReport({
    query,
    displayedProvider,
    resultBlocks,
    additiveResults,
    algoliaSettled,
    reportReason,
    surface,
    limits,
    loadStatus,
  })

  // Flatten the headline scores into top-level scalar attributes so the CDP
  // ingest exposes them as queryable columns (nested objects are only stored as
  // jsonb). The full composed report is sent alongside for deep inspection.
  //
  // The flat columns carry the BASELINE profile only, so every existing
  // dashboard query keeps measuring the same thing. The tuned profile is
  // reported inside `report` instead of being written into the now-unused
  // `fuse*` columns — reusing those would silently relabel two different
  // engines as one and make the historical rows unreadable.
  const { score } = report
  const { overall } = report.content
  analytics.track('Search Quality Report', {
    query,
    displayedProvider: report.displayedProvider,
    scoreOverall: score.overall,
    minisearchQualityScore: score.minisearch.qualityScore,
    minisearchPrecision: score.minisearch.precision,
    minisearchRecall: score.minisearch.recall,
    minisearchRankCorrelation: score.minisearch.rankCorrelation,
    // Total content hit counts per engine.
    algoliaHitCount: overall.hits.algolia,
    minisearchHitCount: overall.hits.minisearch,
    // Membership overlap vs the Algolia gold standard (summed across indices).
    algoliaMinisearchMatching: overall.matching.algoliaMinisearch,
    algoliaMinisearchMissing: overall.missing.algoliaMinisearch,
    algoliaMinisearchExcess: overall.excess.algoliaMinisearch,
    // Was the local library fully loaded when this query ran? Flat so the ship
    // gates can filter on it without unnesting SUPER, and three-valued: null
    // means not yet known, which must not be filtered as if it were false.
    libraryComplete: report.coverage.complete,
    // Why the baseline missed what it missed. notIndexed is a coverage problem,
    // indexed is a tuning problem.
    minisearchMissingIndexed: overall.missingAttribution.indexed,
    minisearchMissingNotIndexed: overall.missingAttribution.notIndexed,
    report,
  })

  searchDebugLog('[searchReporter] Search Quality Report', report)
}
